if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        // Find the submit button - could be .add-to-cart-button (container) or .product-form__submit or button[name="add"]
        this.submitButton = this.form?.querySelector('.add-to-cart-button button, .product-form__submit, button[name="add"]');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();
        
        // Clear error states when users start typing in required fields
        this.initRequiredFieldValidation();
        
        // Intercept button clicks BEFORE form submission (critical for Ella 7 AJAX)
        this.initButtonClickInterception();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initRequiredFieldValidation() {
        // Add event listeners to clear error state when user starts typing in required fields
        // Use document.querySelectorAll to match how properties are collected (they might be outside the form)
        const productFormScope = this.closest('product-form-component');
        const allRequiredFields = document.querySelectorAll('[name^="properties"][required]');
        const scopedRequiredFields = Array.from(allRequiredFields).filter(field => {
          return productFormScope && productFormScope.contains(field);
        });
        
        scopedRequiredFields.forEach(field => {
          // Clear error state on input/change
          const clearError = () => {
            field.removeAttribute('aria-invalid');
            field.classList.remove('field__input--error');
            // Clear error message if field is now valid
            if (field.value && field.value.trim() !== '') {
              this.handleErrorMessage(false);
            }
          };
          
          field.addEventListener('input', clearError);
          field.addEventListener('change', clearError);
        });
      }

      initButtonClickInterception() {
        // Intercept button clicks using capture phase - runs BEFORE other handlers
        // This is critical for Ella 7 which uses AJAX add-to-cart
        
        // Get the product form component scope for validation
        const formScope = this;
        
        // Helper function to validate and handle errors
        const handleValidation = (e, button) => {
          // Validate required fields BEFORE allowing form submission
          const validationError = formScope.validateRequiredFields();
          if (validationError) {
            // CRITICAL: Prevent the event from continuing
            e.preventDefault();
            e.stopImmediatePropagation(); // Stops all other handlers
            e.stopPropagation();
            
            // Show error message
            formScope.handleErrorMessage(validationError.message);
            
            // Focus and highlight the invalid field
            if (validationError.field) {
              validationError.field.focus();
              validationError.field.scrollIntoView({ behavior: 'smooth', block: 'center' });
              validationError.field.setAttribute('aria-invalid', 'true');
              validationError.field.classList.add('field__input--error');
            }
            
            // Re-enable button if it was disabled
            if (button && button.disabled) {
              button.disabled = false;
            }
            
            return false;
          }
          
          // If validation passes, allow the event to continue
          return true;
        };
        
        // Target all possible add-to-cart button selectors within the form
        // Note: .add-to-cart-button is a container, the actual button is inside it
        const buttonSelectors = [
          '.add-to-cart-button button',
          '.add-to-cart-button-container button',
          '.product-form__submit',
          'button[name="add"]',
          '[data-add-to-cart]',
          '.shopify-payment-button__button',
          '[data-buy-now-button]',
          'button[type="submit"]'
        ];
        
        buttonSelectors.forEach(selector => {
          const buttons = this.form?.querySelectorAll(selector) || [];
          buttons.forEach(button => {
            // Use capture phase (true) to intercept BEFORE other handlers
            button.addEventListener('click', function(e) {
              if (!handleValidation(e, button)) {
                return false;
              }
            }, true); // Capture phase = true
          });
        });
        
        // Also intercept on sticky add-to-cart buttons (they might be outside the form)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        if (sectionId) {
          const stickyButtonId = `StickyCart-ProductSubmitButton-${sectionId}`;
          const stickyButton = document.getElementById(stickyButtonId);
          if (stickyButton) {
            stickyButton.addEventListener('click', function(e) {
              if (!handleValidation(e, stickyButton)) {
                return false;
              }
            }, true); // Capture phase
          }
        }
        
        // Also listen at document level for any dynamically added buttons (fallback)
        // But only act if the button is related to this product form
        document.addEventListener('click', function(e) {
          const clickedButton = e.target.closest(buttonSelectors.join(','));
          if (!clickedButton) return;
          
          // Check if this button is related to our form (either inside it or sticky button)
          const isInForm = formScope.form?.contains(clickedButton);
          const isStickyButton = sectionId && clickedButton.id === `StickyCart-ProductSubmitButton-${sectionId}`;
          
          if (isInForm || isStickyButton) {
            if (!handleValidation(e, clickedButton)) {
              return false;
            }
          }
        }, true); // Capture phase
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault(); // Always prevent default form submission (we use AJAX)
        evt.stopPropagation(); // Prevent event bubbling
        
        // Check if button is disabled (null-safe check)
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Manually validate required fields (form has novalidate, so HTML5 validation is disabled)
        // NOTE: Customization fields are OUTSIDE the form element, so we search document-wide
        const validationError = this.validateRequiredFields();
        
        if (validationError) {
          // Stop all propagation if validation fails
          evt.stopImmediatePropagation();
          
          this.handleErrorMessage(validationError.message);
          if (validationError.field) {
            validationError.field.focus();
            validationError.field.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Trigger invalid state styling
            validationError.field.setAttribute('aria-invalid', 'true');
            validationError.field.classList.add('field__input--error');
          }
          // CRITICAL: Return early to prevent any further execution (including the fetch call)
          return false;
        }

        this.handleErrorMessage();

        // Update button state (null-safe checks)
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          const spinner = this.submitButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.remove('hidden');
        }

        // Also reflect loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        const stickyAddButton = sectionId
          ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
          : undefined;
        const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
        if (stickyAddButton && stickyLoadingSpinner) {
          stickyAddButton.classList.add('loading');
          stickyLoadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const properties = document.querySelectorAll('.product-form__input [name^="properties"]')

        properties.forEach(property => {
          if (property.type === 'file') {
            if (!property.files || property.files.length === 0 || !property.files[0]) return;
            formData.append(property.name, property.files[0]);
            return;
          }

          if (property.value == null || property.value === '') return;
          formData.append(property.name, property.value);
        })

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form-component',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => {
                  if (this.cart && typeof this.cart.renderContents === 'function') {
                    this.cart.renderContents(response);
                  }
                });
              },{ once: true });
              quickAddModal.hide(true);
            } else {
              if (this.cart && typeof this.cart.renderContents === 'function') {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

            // Clear loading state on the sticky add-to-cart button (if present)
            const productInfo = this.closest('product-info');
            const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
            const stickyAddButton = sectionId
              ? document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`)
              : undefined;
            const stickyLoadingSpinner = stickyAddButton?.querySelector('.loading__spinner');
            if (stickyAddButton && stickyLoadingSpinner) {
              stickyAddButton.classList.remove('loading');
              stickyLoadingSpinner.classList.add('hidden');
            }
          });
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }


      validateRequiredFields() {
        // Find all required customization fields (inputs, selects, textareas with name starting with "properties")
        // Use document.querySelectorAll because customization fields are OUTSIDE the form element
        // (They're in _product-details block, while form is in buy-buttons block)
        const productFormScope = this.closest('product-form-component');
        
        // Find all required property fields in the document
        const allRequiredFields = document.querySelectorAll('[name^="properties"][required]');
        
        // Filter to only fields within this product form component's scope (same product page)
        const scopedRequiredFields = Array.from(allRequiredFields).filter(field => {
          if (!productFormScope) return false;
          // Check if field is within the same product-form-component or product-info parent
          const productInfo = productFormScope.closest('product-info');
          const fieldProductInfo = field.closest('product-info');
          // Fields must be in the same product-info context
          return productInfo && fieldProductInfo && productInfo === fieldProductInfo;
        });
        
        for (const field of scopedRequiredFields) {
          let isEmpty = false;
          let fieldLabel = '';
          
          // Get the field label for error message
          const labelElement = field.closest('.product-form__input')?.querySelector('.form-label, label');
          if (labelElement) {
            // Remove the asterisk if present and trim
            fieldLabel = labelElement.textContent.replace(/\*/, '').trim();
          }
          
          // Check different field types
          if (field.type === 'file') {
            // For file inputs, check if a file is selected
            isEmpty = !field.files || field.files.length === 0;
          } else if (field.tagName === 'SELECT') {
            // For dropdowns, check if value is empty (placeholder option has empty value)
            isEmpty = !field.value || field.value === '';
          } else if (field.tagName === 'TEXTAREA') {
            // For textareas, check if value is empty or just whitespace
            isEmpty = !field.value || field.value.trim() === '';
          } else if (field.type === 'number') {
            // For number inputs, check if value is empty, invalid, or outside min/max range
            if (field.value === '' || field.value === null) {
              isEmpty = true;
            } else {
              const numValue = parseFloat(field.value);
              const minValue = field.hasAttribute('min') ? parseFloat(field.getAttribute('min')) : null;
              const maxValue = field.hasAttribute('max') ? parseFloat(field.getAttribute('max')) : null;
              
              // Check if value is not a valid number
              if (isNaN(numValue)) {
                isEmpty = true;
              } else {
                // Check min/max range
                if (minValue !== null && numValue < minValue) {
                  // Value is below minimum
                  return {
                    field: field,
                    message: fieldLabel 
                      ? `"${fieldLabel}" must be at least ${minValue}.`
                      : `Value must be at least ${minValue}.`
                  };
                } else if (maxValue !== null && numValue > maxValue) {
                  // Value is above maximum
                  return {
                    field: field,
                    message: fieldLabel 
                      ? `"${fieldLabel}" must be at most ${maxValue}.`
                      : `Value must be at most ${maxValue}.`
                  };
                } else {
                  // Value is valid
                  isEmpty = false;
                }
              }
            }
          } else {
            // For text inputs, check if value is empty or just whitespace
            isEmpty = !field.value || field.value.trim() === '';
          }
          
          if (isEmpty) {
            const errorMessage = fieldLabel 
              ? `Please fill in "${fieldLabel}" field.`
              : 'Please fill in all required fields.';
            
            return {
              field: field,
              message: errorMessage
            };
          }
        }
        
        // Clear any previous error states
        scopedRequiredFields.forEach(field => {
          field.removeAttribute('aria-invalid');
          field.classList.remove('field__input--error');
        });
        
        return null; // All fields are valid
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
